import React from 'react';
import { Box, Typography, Chip, styled } from '@mui/material';
import DynamicTable, { DynamicColumn } from '../ui/DynamicTable';
import CustomAccordion from '../ui/CustomAccordion';
import { STATUS } from 'components/LeadManagement/constants';
import {
  AUDIT_FIELD_KEYS,
  isStatusField,
  isTimestampField,
  isUserField,
  isEventDetailsField,
} from './auditChangesConstants';

export interface AuditChange {
  timeStamp: string;
  user: string;
  processType: string;
  eventType: string;
  eventStatus: string;
  eventDetails: string;
  oldDeDupeStatus: string;
  newDeDupeStatus: string;
  processStatus: string;
}

interface AuditChangesSectionProps {
  auditChanges?: AuditChange[];
  expandedSection: 'deduplication' | 'comments' | 'audit' | 'caregiver' | 'tracking' | null;
  setExpandedSection: (
    section: 'deduplication' | 'comments' | 'audit' | 'caregiver' | 'tracking' | null
  ) => void;
  fieldConfig?: Array<{ key: string; label: string }>;
}

const TableWrapper = styled(Box)({
  overflowX: 'auto',
  mt: 0,
});

const StyledChip = styled(Chip)<{ actionType: string }>(({ actionType }) => {
  const getActionColor = (action: string) => {
    if (!action) {
      return { bg: '#F5F5F5', color: '#616161' };
    }
    switch (action.toLowerCase()) {
      case 'success':
        return { bg: '#E8F5E9', color: '#2E7D32' };
      case 'processed':
      case 'complete':
      case 'completed':
        return { bg: '#E3F2FD', color: '#1565C0' };
      case 'pending':
      case 'in progress':
      case 'processing':
        return { bg: '#FFF3E0', color: '#E65100' };
      case 'failed':
      case 'error':
      case 'rejected':
        return { bg: '#FFEBEE', color: '#C62828' };
      case STATUS.MANUAL_REVIEW_REQUIRED.toLowerCase():
        return { bg: '#FFF3E0', color: '#E65100' };
      case STATUS.AUTO_MERGE.toLowerCase():
        return { bg: '#E3F2FD', color: '#1565C0' };
      case STATUS.NEW_RECORD.toLowerCase():
        return { bg: '#E8F5E9', color: '#2E7D32' };
      default:
        return { bg: '#F5F5F5', color: '#616161' };
    }
  };

  const colors = getActionColor(actionType);
  return {
    backgroundColor: colors.bg,
    color: colors.color,
    fontSize: '11px',
    fontWeight: 600,
    height: '22px',
    textTransform: 'capitalize',
  };
});

const UserCell = styled(Typography)(({ theme }) => ({
  fontSize: '12px',
  color: theme.palette.neutral8?.main,
  fontWeight: 500,
}));

const TimestampCell = styled(Typography)(({ theme }) => ({
  fontSize: '12px',
  color: theme.palette.neutral7?.main,
}));

const AuditChangesSection: React.FC<AuditChangesSectionProps> = ({
  auditChanges,
  expandedSection,
  setExpandedSection,
  fieldConfig,
}) => {
  const defaultColumns: DynamicColumn<AuditChange>[] = [
    {
      id: AUDIT_FIELD_KEYS.EVENT_DETAILS,
      label: 'Event Details',
      accessor: AUDIT_FIELD_KEYS.EVENT_DETAILS,
      width: '250px',
      render: (row) => <Typography fontSize="12px">{row.eventDetails}</Typography>,
    },
    {
      id: AUDIT_FIELD_KEYS.EVENT_STATUS,
      label: 'Event Status',
      accessor: AUDIT_FIELD_KEYS.EVENT_STATUS,
      render: (row) => (
        <StyledChip label={row.eventStatus} size="small" actionType={row.eventStatus} />
      ),
    },
    {
      id: AUDIT_FIELD_KEYS.EVENT_TYPE,
      label: 'Event Type',
      accessor: AUDIT_FIELD_KEYS.EVENT_TYPE,
      render: (row) => <Typography fontSize="12px">{row.eventType}</Typography>,
    },
    {
      id: AUDIT_FIELD_KEYS.OLD_DEDUPE_STATUS,
      label: 'Old Status',
      accessor: AUDIT_FIELD_KEYS.OLD_DEDUPE_STATUS,
      render: (row) => (
        <StyledChip label={row.oldDeDupeStatus} size="small" actionType={row.oldDeDupeStatus} />
      ),
    },
    {
      id: AUDIT_FIELD_KEYS.NEW_DEDUPE_STATUS,
      label: 'New Status',
      accessor: AUDIT_FIELD_KEYS.NEW_DEDUPE_STATUS,
      render: (row) => (
        <StyledChip label={row.newDeDupeStatus} size="small" actionType={row.newDeDupeStatus} />
      ),
    },
    {
      id: AUDIT_FIELD_KEYS.PROCESS_TYPE,
      label: 'Process Type',
      accessor: AUDIT_FIELD_KEYS.PROCESS_TYPE,
      render: (row) => <Typography fontSize="12px">{row.processType}</Typography>,
    },
    {
      id: AUDIT_FIELD_KEYS.PROCESS_STATUS,
      label: 'Process Status',
      accessor: AUDIT_FIELD_KEYS.PROCESS_STATUS,
      render: (row) => (
        <StyledChip label={row.processStatus} size="small" actionType={row.processStatus} />
      ),
    },
    {
      id: AUDIT_FIELD_KEYS.TIMESTAMP,
      label: 'Timestamp',
      accessor: AUDIT_FIELD_KEYS.TIMESTAMP,
      render: (row) => <TimestampCell>{row.timeStamp}</TimestampCell>,
    },
    {
      id: AUDIT_FIELD_KEYS.USER,
      label: 'User',
      accessor: AUDIT_FIELD_KEYS.USER,
      render: (row) => <UserCell>{row.user}</UserCell>,
    },
  ];

  // Build columns from Sitecore config if available
  const columns: DynamicColumn<AuditChange>[] =
    fieldConfig && fieldConfig.length > 0
      ? fieldConfig.map((field) => {
          const column: DynamicColumn<AuditChange> = {
            id: field.key,
            label: field.label,
            accessor: field.key as keyof AuditChange,
          };

          if (isEventDetailsField(field.key)) {
            column.width = '250px';
          }

          if (isTimestampField(field.key)) {
            column.render = (row) => <TimestampCell>{row.timeStamp}</TimestampCell>;
          } else if (isUserField(field.key)) {
            column.render = (row) => <UserCell>{row.user}</UserCell>;
          } else if (isStatusField(field.key)) {
            column.render = (row) => {
              const value = (row as any)[field.key] || '-';
              return <StyledChip label={value} size="small" actionType={value} />;
            };
          } else {
            column.render = (row) => (
              <Typography fontSize="12px">{(row as any)[field.key] || '-'}</Typography>
            );
          }
          return column;
        })
      : defaultColumns;

  return (
    <CustomAccordion
      title="Audit Changes"
      count={auditChanges?.length}
      expanded={expandedSection === 'audit'}
      onChange={() => setExpandedSection(expandedSection === 'audit' ? null : 'audit')}
      detailsSx={{ p: 0 }}
    >
      <TableWrapper>
        <DynamicTable
          columns={columns}
          data={auditChanges || []}
          variant="compact"
          noDataMessage="No audit changes recorded for this record."
        />
      </TableWrapper>
    </CustomAccordion>
  );
};

export default AuditChangesSection;
